import { FormattedMessage } from 'react-intl';
import { ComponentProps, Dispatch, SetStateAction, useState } from 'react';
import type CurrencyCode from '@alltrails/core/types/CurrencyCode';
import type { Plan } from '@alltrails/types/Plan';
import type CountryCode from '@alltrails/core/types/CountryCode';
import SectionDivider from '@alltrails/denali/components/SectionDivider';
import type Coupon from '@alltrails/types/Coupon';
import type PurchaseTier from '@alltrails/core/types/PurchaseTier';
import type { ApplePayConfig } from '@recurly/recurly-js';
import type WebProPurchaseLocation from '@alltrails/amplitude/enums/WebProPurchaseLocation';
import { useExperiments } from '@alltrails/experiments';
import type { CardElementChangeEvent } from '../../types/ReactRecurly';
import { CARD_VALIDATION_NAMES, TokenValidationErrors } from '../../types/RecurlyTokenization';
import TokenizedCreditCardForm from '../TokenizedCreditCardForm';
import type ProResponse from '../../types/ProResponse';
import ApplePayButton from './ApplePayButton';
import ErrorText from './ErrorText';
import styles from './PaymentForm.module.scss';
import PayPalButton from './PayPalButton';
import GooglePayButton from './GooglePayButton';
import LoadingOverlay from './LoadingOverlay';
import ApplePayButtonWithSDK from './ApplePayButtonWithSDK';

type Props = {
  applePayConfig: ApplePayConfig;
  billingCountry: CountryCode;
  browserName?: string;
  coupon?: Coupon;
  currencyCode: CurrencyCode;
  errors?: ProResponse['errors'];
  isComplianceCheckboxEnabled?: boolean;
  isUpgrade?: boolean;
  onApplePayToken: ComponentProps<typeof ApplePayButton>['onApplePayToken'];
  onGooglePayToken: ComponentProps<typeof GooglePayButton>['onGooglePayToken'];
  onPayPalToken: ComponentProps<typeof PayPalButton>['onPayPalToken'];
  plan: Plan;
  setErrors: Dispatch<SetStateAction<ProResponse['errors'] | undefined>>;
  onBillingCountryChange: (countryCode: CountryCode, arg1: any) => void;
  setClientValidationErrors: (inputName: CARD_VALIDATION_NAMES, value: string | CardElementChangeEvent) => void;
  validationErrors: TokenValidationErrors;
  purchaseTier: PurchaseTier;
  webProPurchaseLocation: WebProPurchaseLocation;
};

// https://developer.paypal.com/docs/reports/reference/paypal-supported-currencies/
const payPalSupportedCurrencies = [
  'AUD',
  'BRL',
  'CAD',
  'CNY',
  'CZK',
  'DKK',
  'EUR',
  'HKD',
  'HUF',
  'ILS',
  'JPY',
  'MYR',
  'MXN',
  'TWD',
  'NZD',
  'NOK',
  'PHP',
  'PLN',
  'GBP',
  'SGD',
  'SEK',
  'CHF',
  'THB',
  'USD'
];

export default function PaymentForm({
  applePayConfig,
  billingCountry,
  browserName,
  coupon,
  currencyCode,
  isComplianceCheckboxEnabled = false,
  isUpgrade,
  onApplePayToken,
  onGooglePayToken,
  onPayPalToken,
  plan,
  errors,
  setErrors,
  onBillingCountryChange,
  validationErrors,
  setClientValidationErrors,
  purchaseTier,
  webProPurchaseLocation
}: Props) {
  // this submitting state is used for non-creditcard services
  const [submitting, setSubmitting] = useState<boolean>(false);

  const experiments = useExperiments();

  const productType = purchaseTier === 'peak' ? 'peak' : 'plus';

  return (
    <>
      <div className={styles.grid}>
        {experiments['web-growth-rm-apple-pay-sdk']?.value === 'treatment' ? (
          <ApplePayButtonWithSDK
            applePayConfig={applePayConfig}
            coupon={coupon}
            currencyCode={currencyCode}
            onApplePayToken={onApplePayToken}
            plan={plan}
            productType={productType}
            setErrors={setErrors}
            setSubmitting={setSubmitting}
            webProPurchaseLocation={webProPurchaseLocation}
          />
        ) : (
          <ApplePayButton
            applePayConfig={applePayConfig}
            coupon={coupon}
            currencyCode={currencyCode}
            onApplePayToken={onApplePayToken}
            plan={plan}
            productType={productType}
            setErrors={setErrors}
            setSubmitting={setSubmitting}
            webProPurchaseLocation={webProPurchaseLocation}
          />
        )}
        <GooglePayButton
          coupon={coupon}
          currencyCode={currencyCode}
          onGooglePayToken={onGooglePayToken}
          plan={plan}
          productType={productType}
          setErrors={setErrors}
          setSubmitting={setSubmitting}
          webProPurchaseLocation={webProPurchaseLocation}
        />
        {payPalSupportedCurrencies.includes(currencyCode) && browserName !== 'WebKit' && (
          <PayPalButton
            coupon={coupon}
            onPayPalToken={onPayPalToken}
            plan={plan}
            productType={productType}
            purchaseTier={purchaseTier}
            setErrors={setErrors}
            setSubmitting={setSubmitting}
            submitting={submitting}
            webProPurchaseLocation={webProPurchaseLocation}
          />
        )}
      </div>
      <div>
        <SectionDivider>
          <FormattedMessage defaultMessage="Or enter payment details" />
        </SectionDivider>
      </div>
      <TokenizedCreditCardForm
        billingCountry={{
          onChange: onBillingCountryChange,
          value: billingCountry
        }}
        handleInputChange={(input, value: string | unknown) => {
          if (errors) {
            setErrors(undefined);
          }

          setClientValidationErrors(input, value as string | CardElementChangeEvent);
        }}
        isComplianceCheckboxEnabled={isComplianceCheckboxEnabled}
        isCouponInputExpanded={false}
        isCouponInputShown={false}
        isUpgrade={isUpgrade}
        purchaseTier={purchaseTier}
        validationErrors={validationErrors}
      />
      {errors && <ErrorText errors={errors} />}
      {submitting && <LoadingOverlay />}
    </>
  );
}
